/**
 * Creare o modificare un partner: chi è, dati fiscali e di pagamento, profilo di compenso, regime e frequenza.
 * Scrive solo il server (`partnerConsoleSalvaPartner`), che rifà tutti i controlli.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { FREQUENZE_DI_LIQUIDAZIONE } from '@shared/partner/compensoPartner';
import type { PartnerDoc, ProfiloCompensoDoc, RegimeFiscaleDoc, TipoSoggettoPartner } from '@shared/partner/tipi';
import { salvaPartner, type ConId, type RichiestaPartner } from '../../services/partnerConsoleService';

const SOGGETTI: TipoSoggettoPartner[] = ['societa_di_capitali', 'societa_di_persone', 'persona_fisica'];
const LINGUE = ['it', 'en'] as const;

interface Props {
  aperto: boolean;
  /** `null` = nuovo partner. */
  partner: ConId<PartnerDoc> | null;
  profili: Array<ConId<ProfiloCompensoDoc>>;
  regimi: Array<ConId<RegimeFiscaleDoc>>;
  onChiudi: () => void;
  /** Dopo un salvataggio riuscito (la pagina ricarica e lo dice). */
  onSalvato: () => void;
  onErrore: (messaggio: string) => void;
}

function vuoto(profili: Props['profili']): RichiestaPartner {
  return {
    // Forma e regime li si lascia vuoti: i dati fiscali li compila il partner (03/11/2026), il regime si sceglie dopo.
    nome: '', email: '', lingua: 'it', paese: 'IT', tipo_soggetto: null,
    ragione_sociale: '', partita_iva: '', codice_fiscale: '', iban: '', telefono: '', note: '',
    profilo_compenso_id: profili.find((p) => p.attivo)?.id ?? '',
    regime_fiscale_id: null,
    liquidazione_ogni_mesi: null,
    senza_provvigioni: false,
  };
}

function daPartner(p: ConId<PartnerDoc>): RichiestaPartner {
  return {
    partner_id: p.id, nome: p.nome, email: p.email, lingua: p.lingua, paese: p.paese, tipo_soggetto: p.tipo_soggetto,
    ragione_sociale: p.ragione_sociale ?? '', partita_iva: p.partita_iva ?? '', codice_fiscale: p.codice_fiscale ?? '',
    iban: p.iban ?? '', telefono: p.telefono ?? '', note: p.note ?? '', profilo_compenso_id: p.profilo_compenso_id,
    regime_fiscale_id: p.regime_fiscale_id, liquidazione_ogni_mesi: p.liquidazione_ogni_mesi ?? null,
    senza_provvigioni: p.senza_provvigioni === true,
    persona_rete_id: p.persona_rete_id ?? null,
  };
}

export const DialogoPartner: React.FC<Props> = ({ aperto, partner, profili, regimi, onChiudi, onSalvato, onErrore }) => {
  const { t } = useTranslation('partner');
  const [f, setF] = useState<RichiestaPartner>(() => vuoto(profili));
  const [lavora, setLavora] = useState(false);

  useEffect(() => {
    if (aperto) setF(partner ? daPartner(partner) : vuoto(profili));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperto, partner?.id]);

  const set = <K extends keyof RichiestaPartner>(k: K, v: RichiestaPartner[K]) => setF((x) => ({ ...x, [k]: v }));
  // Profili e regimi spenti restano scelti se il partner li ha già: spegnere non toglie niente a nessuno.
  const profiliScelti = useMemo(() => profili.filter((p) => p.attivo || p.id === f.profilo_compenso_id), [profili, f.profilo_compenso_id]);
  const regimiScelti = useMemo(() => regimi.filter((r) => r.attivo || r.id === f.regime_fiscale_id), [regimi, f.regime_fiscale_id]);
  const emailBloccata = !!partner?.uid;
  const completo = f.nome.trim().length > 1 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && (f.senza_provvigioni || !!f.profilo_compenso_id) && /^[A-Za-z]{2}$/.test(f.paese);

  const salva = async () => {
    setLavora(true);
    try {
      await salvaPartner({ ...f, paese: f.paese.toUpperCase() });
      onSalvato();
      onChiudi();
    } catch (e) {
      onErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setLavora(false);
    }
  };

  const testo = (k: keyof RichiestaPartner, etichetta: string, extra: Partial<React.ComponentProps<typeof TextField>> = {}) => (
    <TextField
      label={etichetta}
      value={(f[k] as string | undefined) ?? ''}
      onChange={(e) => set(k, e.target.value as never)}
      size="small"
      fullWidth
      {...extra}
    />
  );

  const sezione = (titolo: string) => (
    <Grid size={12}>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: tokens.color.graphite, mt: 1 }}>
        {titolo}
      </Typography>
    </Grid>
  );

  return (
    <Dialog open={aperto} onClose={lavora ? undefined : onChiudi} fullWidth maxWidth="md">
      <DialogTitle>{partner ? t('dialogo_partner.titolo_modifica') : t('dialogo_partner.titolo_nuovo')}</DialogTitle>
      <DialogContent>
        {(profiliScelti.length === 0 || regimiScelti.length === 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>{t('messaggi.serve_profilo_e_regime')}</Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {sezione(t('dialogo_partner.sezione_chi'))}
          <Grid size={{ xs: 12, md: 6 }}>{testo('nome', t('dialogo_partner.nome'), { required: true })}</Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {testo('email', t('dialogo_partner.email'), {
              required: true,
              type: 'email',
              disabled: emailBloccata,
              helperText: emailBloccata ? t('dialogo_partner.email_bloccata') : t('dialogo_partner.email_aiuto'),
            })}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select label={t('dialogo_partner.lingua')} value={f.lingua} onChange={(e) => set('lingua', e.target.value)} size="small" fullWidth>
              {LINGUE.map((l) => <MenuItem key={l} value={l}>{t(`lingue.${l}`)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            {testo('paese', t('dialogo_partner.paese'), { helperText: t('dialogo_partner.paese_aiuto'), inputProps: { maxLength: 2 } })}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>{testo('telefono', t('dialogo_partner.telefono'))}</Grid>

          {sezione(t('dialogo_partner.sezione_fisco'))}
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              label={t('dialogo_partner.tipo_soggetto')}
              value={f.tipo_soggetto ?? ''}
              onChange={(e) => set('tipo_soggetto', e.target.value === '' ? null : (e.target.value as TipoSoggettoPartner))}
              size="small"
              fullWidth
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">{t('dialogo_partner.soggetto_dal_partner')}</MenuItem>
              {SOGGETTI.map((s) => <MenuItem key={s} value={s}>{t(`soggetto.${s}`)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>{testo('ragione_sociale', t('dialogo_partner.ragione_sociale'))}</Grid>
          <Grid size={{ xs: 12, md: 4 }}>{testo('partita_iva', t('dialogo_partner.partita_iva'))}</Grid>
          <Grid size={{ xs: 12, md: 4 }}>{testo('codice_fiscale', t('dialogo_partner.codice_fiscale'))}</Grid>
          <Grid size={{ xs: 12, md: 4 }}>{testo('iban', t('dialogo_partner.iban'))}</Grid>

          {sezione(t('dialogo_partner.sezione_compenso'))}
          <Grid size={12}>
            <FormControlLabel
              control={<Checkbox checked={f.senza_provvigioni} onChange={(e) => set('senza_provvigioni', e.target.checked)} size="small" />}
              label={t('dialogo_partner.senza_provvigioni')}
            />
            <Typography variant="caption" sx={{ display: 'block', color: tokens.color.ash, ml: 4 }}>{t('dialogo_partner.senza_provvigioni_aiuto')}</Typography>
          </Grid>
          {!f.senza_provvigioni && (<>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select label={t('dialogo_partner.profilo')} value={f.profilo_compenso_id} onChange={(e) => set('profilo_compenso_id', e.target.value)} size="small" fullWidth required>
              {profiliScelti.map((p) => <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              label={t('dialogo_partner.regime')}
              value={f.regime_fiscale_id ?? ''}
              onChange={(e) => set('regime_fiscale_id', e.target.value === '' ? null : e.target.value)}
              size="small"
              fullWidth
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">{t('dialogo_partner.regime_dopo')}</MenuItem>
              {regimiScelti.map((r) => <MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              label={t('dialogo_partner.frequenza')}
              value={f.liquidazione_ogni_mesi ?? ''}
              onChange={(e) => set('liquidazione_ogni_mesi', e.target.value === '' ? null : Number(e.target.value))}
              size="small"
              fullWidth
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">{t('frequenze.piattaforma')}</MenuItem>
              {FREQUENZE_DI_LIQUIDAZIONE.map((m) => <MenuItem key={m} value={m}>{t('frequenze.mesi', { count: m })}</MenuItem>)}
            </TextField>
          </Grid>
          </>)}
          <Grid size={12}>{testo('note', t('dialogo_partner.note'), { multiline: true, minRows: 2 })}</Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={lavora} sx={{ textTransform: 'none' }}>{t('azioni.annulla')}</Button>
        <Button
          variant="contained"
          onClick={salva}
          disabled={!completo || lavora}
          startIcon={lavora ? <CircularProgress size={14} /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {t('azioni.salva')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
